import React from "react";
import { DayOfWeek, ShiftConfigPayload } from "../../util/types";
import { PlusIcon } from "./icons";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";

interface DayShiftCardProps {
  day: DayOfWeek;
  shifts: ShiftConfigPayload[];
  onConfigure: (day: DayOfWeek) => void;
  onDelete?: (day: DayOfWeek) => void;
  hasUnsavedChanges?: boolean;
}

const DayShiftCard: React.FC<DayShiftCardProps> = ({
  day,
  shifts,
  onConfigure,
  onDelete,
  hasUnsavedChanges,
}) => {
  const totalAgents = shifts.reduce(
    (sum, shift) => sum + shift.requiredAgents,
    0,
  );
  const hasShifts = shifts.length > 0;

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 transition-all duration-200 ${
        hasShifts ? "border-l-4 border-indigo-500 dark:border-indigo-400" : "border border-gray-200 dark:border-gray-700"
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">{day}</h3>
        <div className="flex gap-2">
          {hasShifts && (
            <button
              onClick={() => onConfigure(day)}
              className="group p-1.5 flex items-center justify-center bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-blue-600 hover:border-blue-600 dark:hover:bg-blue-600 dark:hover:border-blue-600 transition-colors shadow-sm cursor-pointer"
              title="Edit Shifts"
            >
              <FaEdit size={16} className="text-blue-600 dark:text-blue-400 group-hover:text-white dark:group-hover:text-white group-hover:scale-110 transition-all" />
            </button>
          )}

          {hasShifts && onDelete && !hasUnsavedChanges && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(day);
              }}
              className="group p-1.5 flex items-center justify-center bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors shadow-sm cursor-pointer"
              title="Delete Day Configuration"
            >
              <FaRegTrashAlt size={16} className="text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
            </button>
          )}
        </div>
      </div>

      {hasShifts ? (
        <div className="space-y-1.5">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-semibold">{shifts.length}</span> shift
            {shifts.length > 1 ? "s" : ""} configured
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-semibold">{totalAgents}</span> agents needed
          </div>
          <div className="mt-2 space-y-1">
            {shifts.map((shift, index) => (
              <div key={index} className="text-xs bg-gray-50 dark:bg-gray-700 rounded p-1.5">
                <div className="font-medium text-gray-700 dark:text-gray-200">
                  {shift.shiftName}
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  {shift.startHour}:00 - {shift.endHour}:00 •{" "}
                  {shift.requiredAgents} agents
                </div>
                <div className="mt-1">
                  {shift.onlyMale && (
                    <span className="inline-block px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded">
                      👨 Male Only
                    </span>
                  )}
                  {shift.onlyFemale && (
                    <span className="inline-block px-2 py-0.5 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 text-xs rounded">
                      👩 Female Only
                    </span>
                  )}
                  {shift.bothGenderAllowed && (
                    <span className="inline-block px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded">
                      👥 Both Genders
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-gray-400 dark:text-gray-500 text-sm mb-2">No shifts configured</p>
          <button
            onClick={() => onConfigure(day)}
            className="inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium"
          >
            <PlusIcon />
            <span className="ml-1">Add Shifts</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default DayShiftCard;
